import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import NewsletterSignup from "./NewsletterSignup";
import { useLang, useSettings } from "../lib/providers";

/**
 * Exit-intent popup for newsletter signup.
 *
 * Anti-overlap rules (to NOT clash with existing FomoPopup):
 *  - Only shown once per session (sessionStorage flag "newsletter_popup_shown")
 *  - Won't appear if another popup is already showing in this session
 *    (shared key "site_popup_shown")
 *  - Won't appear on /panier or /merci/* (the user is mid-transaction)
 *  - Triggered only when mouse leaves the top of the viewport (desktop) OR
 *    after 25 seconds of inactivity (mobile fallback).
 */
export default function NewsletterExitIntent() {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);

  const enabled = settings?.newsletter_enabled !== false && settings?.newsletter_popup_enabled !== false;

  useEffect(() => {
    if (!enabled) return;
    // Avoid showing on transactional pages
    const path = window.location.pathname || "";
    if (path.startsWith("/panier") || path.startsWith("/merci") || path.startsWith("/virement") || path.startsWith("/newsletter")) return;
    // Skip if any popup already shown this session
    if (sessionStorage.getItem("site_popup_shown") || sessionStorage.getItem("newsletter_popup_shown")) return;

    let timer;
    const trigger = () => {
      if (sessionStorage.getItem("site_popup_shown")) return;
      sessionStorage.setItem("site_popup_shown", "1");
      sessionStorage.setItem("newsletter_popup_shown", "1");
      setOpen(true);
    };

    // Mobile fallback: trigger after 25s of any activity
    timer = window.setTimeout(trigger, 25000);

    // Desktop: exit-intent (mouse leaves the viewport from the top)
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY < 10) trigger();
    };
    document.addEventListener("mouseout", onMouseOut);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [enabled]);

  if (!open) return null;

  const title = lang === "ar"
    ? (settings?.newsletter_popup_title_ar || "🎾 -10% على طلبك الأول!")
    : (settings?.newsletter_popup_title_fr || "🎾 -10% sur votre 1ère commande !");
  const body = lang === "ar"
    ? (settings?.newsletter_popup_body_ar || "اشترك واحصل على كود الخصم الخاص بك عبر البريد الإلكتروني.")
    : (settings?.newsletter_popup_body_fr || "Inscrivez-vous et recevez votre code promo personnel par email.");
  const minOrder = Number(settings?.newsletter_promo_min_order_dzd || 0);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      data-testid="newsletter-exit-intent"
    >
      <div
        className="relative bg-black border-2 border-neon max-w-md w-full p-6 sm:p-8 animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
        dir={lang === "ar" ? "rtl" : "ltr"}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-white/60 hover:text-clay hover:bg-white/5"
          aria-label="Fermer"
          data-testid="newsletter-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        <p className="text-[10px] uppercase tracking-widest text-neon font-bold mb-2">
          {lang === "ar" ? "✋ انتظر !" : "✋ Attendez !"}
        </p>
        <h2 className="font-heading font-black text-2xl sm:text-3xl mb-3 uppercase tracking-tight">{title}</h2>
        <p className="text-sm text-white/70 mb-2">{body}</p>
        {minOrder > 0 && (
          <p className="text-xs text-neon/90 font-bold mb-4" data-testid="newsletter-min-order-exit">
            ⚠️ {lang === "ar"
              ? `الكود صالح ابتداء من ${minOrder.toLocaleString("fr-FR")} دج من المشتريات`
              : `Code valable dès ${minOrder.toLocaleString("fr-FR")} DZD d'achat`}
          </p>
        )}

        <NewsletterSignup variant="popup" source="exit_intent" />

        <p className="text-[10px] text-white/40 mt-4 text-center">
          {lang === "ar" ? "إلغاء الاشتراك بنقرة واحدة في كل بريد إلكتروني" : "Désinscription en 1 clic dans chaque email"}
        </p>
      </div>
    </div>
  );
}
